import { reachGoal } from 'utils/metrica';
import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import isEqual from 'lodash/isEqual';

import AdditionalServices from "./components/additional_services";
import ModalSearch from "./components/modalSearch";
import ModalConfirm from "../../../../common/modalConfirm";

import {useGetCalcIdFromRoute} from "../../../../../utils/hooks";
import {useDemoAdditionalSave} from 'components/cabinet/demo/steps/additional-services/use-additional-services';
import ModalBig from "../../../../common/modalBig";

import {
  useExtraServicesId,
  useGetExtraServicesByOrderIdQuery,
  useGetExtraServicesSearchByOrderIdMutation,
  useRemoveExtraServicesByOrderIdMutation, useSetCompletedByOrderIdMutation,
  useSetExtraServicesByOrderIdMutation, useSummaryCompleted, useUserExtraServices,
} from "../../../../../api/calc";

import './additional.scss';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import {isCalculationViewModeError} from 'utils/helpers';
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../calculation-read-only';

type Props = {
  handleComplete(): void
}

const Additional = ({ handleComplete }: Props) => {
  const orderId = useGetCalcIdFromRoute();
  const { t } = useTranslation();

  const demoPlayback = useDemoAdditionalSave()
  const readOnly = useCalculationReadOnly()

  const [ companies, setCompanies ] = React.useState<any>();
  const [ totalServicesCost, setTotalServicesCost ] = React.useState<number>();
  const [ saveStepIsActive, setSaveStepIsActive ] = React.useState(false);

  const [ shouldOpenModalSearchAddServices, setShouldOpenModalSearchAddServices ] = useState(false);
  const [ shouldOpenModalSmallConfirm, setShouldOpenModalSmallConfirm ] = useState(false);
  const [ initialValuesExtraServices, setInitialValuesExtraServices ] = useState<number[]>([]);

  const { refetch: refetchGetExtraServices } = useGetExtraServicesByOrderIdQuery(orderId);

  const [ getExtraServices ] = useGetExtraServicesSearchByOrderIdMutation();
  const [ setExtraServices ] = useSetExtraServicesByOrderIdMutation();
  const [ removeExtraServices ] = useRemoveExtraServicesByOrderIdMutation();
  const [ setCompleted ] = useSetCompletedByOrderIdMutation();

  const userServices = useExtraServicesId() ?? [];
  const selectedServices = useUserExtraServices();

  useEffect(() => {
    if (initialValuesExtraServices.length === 0 && userServices.length > 0) {
      setInitialValuesExtraServices(userServices);
    }
  }, [ userServices, initialValuesExtraServices.length ]);

  const handleCloseWithoutSaving = async () => {
    setShouldOpenModalSmallConfirm(false);
    setShouldOpenModalSearchAddServices(false);
    refetchGetExtraServices();
  };

  const handleCloseConfirmModal = async () => {
    setShouldOpenModalSmallConfirm(false);
  };

  const handleChoose = async () => {
    const result = await setExtraServices({
      orderId,
      service_id: userServices,
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    setShouldOpenModalSmallConfirm(false);
    setShouldOpenModalSearchAddServices(false);
    setInitialValuesExtraServices(userServices);

    const removedServices =
        initialValuesExtraServices.filter((id) => !userServices.includes(id)) ?? [];

    if (removedServices.length > 0) {
      await removeExtraServices({
        orderId,
        service_id: removedServices,
      });
    }

    await refetchGetExtraServices();
  };

  const handleSearch = async () => {
    await getExtraServices({ orderId }).then((data) => {
      // @ts-ignore
      setCompanies(data?.data);
    });
    setShouldOpenModalSearchAddServices(true);
  };

  const handleSaveStep = async () => {
    if (demoPlayback.isDemoMode) {
      if (saveStepIsActive) {
        handleComplete();
        demoPlayback.complete()
      }

      return
    }

    reachGoal({
      goal: 'dop_uslugi',
      params: {
        services: selectedServices?.map(company => ({
          company: company.name,
          services: company.services.map(service => ({
            name: service.name,
            price: service.price,
          })),
        })),
        skipped: userServices.length === 0,
      },
    });

    const result = await setCompleted({
      orderId,
      additional_services: true,
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    if (saveStepIsActive) {
      handleComplete();
    }
  };

  const handleOpenConfirmModal = () => {
    const hasChanged = !isEqual(userServices, initialValuesExtraServices);

    if (hasChanged) {
      setShouldOpenModalSmallConfirm(true);
    } else {
      setShouldOpenModalSearchAddServices(false);
    }
  };

  return (
    <>
      <AdditionalServices
        handleOpenModal={handleSearch}
        readOnly={readOnly}
        refetchGetExtraServices={refetchGetExtraServices}
        setInitialValuesExtraServices={setInitialValuesExtraServices}
        setSaveStepIsActive={setSaveStepIsActive}
      />

      <Button
        {...demoPlayback.buttonProps}
        variant='contained'
        className={cx({ 'read-only-action-button': readOnly })}
        startIcon={<CheckIcon />}
        endIcon={readOnlyButtonEndIcon(readOnly)}
        fullWidth
        onClick={readOnly ? undefined : handleSaveStep}
        sx={{ marginTop: 3 }}
        disabled={readOnly || !saveStepIsActive}
        title={readOnly ? t('calc__message__lockedByApplication') : undefined}
      >
        <Typography component='span' variant='inherit'>{formatReferenceCopy(t('calc__text__additional__saveAdditionalServices'))}</Typography>
      </Button>

      {shouldOpenModalSearchAddServices && <ModalBig
        isOpen={Boolean(shouldOpenModalSearchAddServices)}
        onClose={handleOpenConfirmModal}
        title={t('calc__text__additional__searchForAdditionalServices')}
        isSearch
      >
        <ModalSearch
          preloadCompanies={companies}
          refetchGetExtraServices={refetchGetExtraServices}
          handleClose={handleOpenConfirmModal}
          setTotalServicesCost={setTotalServicesCost}
          setInitialValuesExtraServices={setInitialValuesExtraServices}
          handleSave={handleChoose}
        />
      </ModalBig>}

      <ModalConfirm
        isOpen={shouldOpenModalSmallConfirm}
        title={t('calc__text__additional__selectedServices')}
        description={<>
          {t('calc__text__additional__selectedServices')} ({userServices?.length}){' '}
          {t('calc__text__additional__forTheAmountOf')}{' '}
          <span className='additional-title__bold'>{totalServicesCost} USD</span>{' '}
          {t('calc__text__additional__willNotBeSaved')}.{' '}
          {t('calc__text__additional__doYouWantToGetOut')}
        </>}
        handleCloseWithoutSaving={handleCloseWithoutSaving}
        handleClose={handleCloseConfirmModal}
        handleSave={handleChoose}
      />
    </>
  );
};

export default Additional
